"use client"

import { useEffect, useRef, useState } from "react"
import { useRouter } from "next/navigation"
import { captureAnalyticsEvent } from "@/lib/analytics/posthog-client"
import { setGlsHomeDelivery } from "@/lib/data/gls"
import {
  formatGlsDeliveryPointLabel,
  isGlsHomeOption,
} from "@/lib/util/gls-shipping"
import { HttpTypes } from "@medusajs/types"
import { RadioGroup, Radio as RadioGroupOption } from "@headlessui/react"
import { Text, clx } from "@medusajs/ui"
import { useExpressCart } from "@/modules/express-checkout/providers/express-cart"
import GlsDeliveryPointPicker from "@/modules/checkout/components/gls-delivery-point-picker"
import GlsFragileItemsNotice from "@/modules/checkout/components/gls-fragile-items-notice"
import { useGlsShippingState } from "@/modules/checkout/hooks/use-gls-shipping"
import Card from "../card"
import Button from "@/modules/common/components/button"
import Radio from "@/modules/common/components/radio"
import { convertToLocale } from "@/lib/util/money"
import { sdk } from "@/lib/config"

type ExpressShippingProps = {
  countryCode: string
  productHandle: string
  isActive: boolean
  isCompleted: boolean
}

/**
 * Shipping step in express checkout
 * Allows selection of shipping method
 */
const ExpressShipping = ({
  countryCode,
  productHandle,
  isActive,
  isCompleted,
}: ExpressShippingProps) => {
  const router = useRouter()
  const { cart, setShippingMethod, refreshCart, isLoading } = useExpressCart()

  const [shippingOptions, setShippingOptions] = useState<
    HttpTypes.StoreCartShippingOption[]
  >([])
  const [selectedMethodId, setSelectedMethodId] = useState<string>("")
  const [loadingOptions, setLoadingOptions] = useState(false)
  const completedStepRef = useRef(false)

  // Get current shipping method
  const currentShippingMethod = cart?.shipping_methods?.at(-1)

  // Fetch shipping options when cart is available and step is active
  useEffect(() => {
    if (!cart?.id || !isActive) return

    const fetchShippingOptions = async () => {
      setLoadingOptions(true)
      try {
        const { shipping_options } = await sdk.store.fulfillment.listCartOptions(
          { cart_id: cart.id },
          {}
        )
        setShippingOptions(shipping_options || [])

        // Pre-select current method or first option
        if (currentShippingMethod?.shipping_option_id) {
          setSelectedMethodId(currentShippingMethod.shipping_option_id)
        } else if (shipping_options?.[0]?.id) {
          setSelectedMethodId(shipping_options[0].id)
        }
      } catch (error) {
        console.error("Error fetching shipping options:", error)
        setShippingOptions([])
      } finally {
        setLoadingOptions(false)
      }
    }

    fetchShippingOptions()
  }, [cart?.id, isActive, currentShippingMethod?.shipping_option_id])

  const selectedOption = shippingOptions.find((o) => o.id === selectedMethodId)
  const { requiresParcelPoint, selectedPoint, canContinue } = useGlsShippingState(
    cart,
    shippingOptions,
    selectedMethodId
  )

  const handleSelectMethod = async (methodId: string) => {
    setSelectedMethodId(methodId)

    const option = shippingOptions.find((entry) => entry.id === methodId)
    if (currentShippingMethod?.shipping_option_id !== methodId) {
      await setShippingMethod(methodId)
    }

    if (isGlsHomeOption(option) && cart) {
      await setGlsHomeDelivery(cart.id)
      await refreshCart()
    }
  }

  const handleContinue = async () => {
    if (!selectedMethodId || !cart || !canContinue) return

    const selectedShippingOption = shippingOptions.find(
      (option) => option.id === selectedMethodId
    )

    if (currentShippingMethod?.shipping_option_id !== selectedMethodId) {
      await setShippingMethod(selectedMethodId)
    }

    captureAnalyticsEvent("shipping_method_selected", {
      shipping_method_id: selectedMethodId,
      shipping_method_name: selectedShippingOption?.name ?? null,
    })
    captureAnalyticsEvent("checkout_step_completed", {
      step: "payment",
      checkout_type: "express",
    })
    completedStepRef.current = true

    router.push(
      `/${countryCode}/products/${productHandle}?express=true&step=payment`
    )
  }

  const handleEdit = () => {
    router.push(
      `/${countryCode}/products/${productHandle}?express=true&step=shipping`
    )
  }

  useEffect(() => {
    if (isActive) {
      completedStepRef.current = false
    }
  }, [isActive])

  useEffect(() => {
    if (!isActive) {
      return
    }

    return () => {
      if (!completedStepRef.current) {
        captureAnalyticsEvent("checkout_step_abandoned", {
          step: "shipping",
          checkout_type: "express",
        })
      }
    }
  }, [isActive])

  return (
    <Card
      title="3. Način dostave"
      isActive={isActive}
      isCompleted={isCompleted}
      onEdit={handleEdit}
    >
      {isActive ? (
        <div className="flex flex-col gap-4">
          {loadingOptions ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-neutral-900" />
            </div>
          ) : shippingOptions.length === 0 ? (
            <div className="text-center py-4 text-neutral-500">
              Ni razpoložljivih načinov dostave za vaš naslov.
            </div>
          ) : (
            <RadioGroup value={selectedMethodId} onChange={handleSelectMethod}>
              <div className="flex flex-col gap-2">
                {shippingOptions.map((option) => (
                  <RadioGroupOption
                    key={option.id}
                    value={option.id}
                    className={clx(
                      "flex items-center justify-between p-4 rounded-lg border-2 cursor-pointer transition-all",
                      {
                        "border-neutral-900 bg-neutral-50":
                          selectedMethodId === option.id,
                        "border-neutral-200 hover:border-neutral-300":
                          selectedMethodId !== option.id,
                      }
                    )}
                  >
                    <div className="flex items-center gap-3">
                      <Radio checked={selectedMethodId === option.id} />
                      <div className="flex flex-col">
                        <span className="font-medium">{option.name}</span>
                      </div>
                    </div>
                    <span className="font-semibold">
                      {convertToLocale({
                        amount: option.amount || 0,
                        currency_code: cart?.currency_code || "EUR",
                      })}
                    </span>
                  </RadioGroupOption>
                ))}
              </div>
            </RadioGroup>
          )}

          {shippingOptions.length > 0 && (
            <GlsFragileItemsNotice
              shippingOptions={shippingOptions}
              selectedMethodId={selectedMethodId}
            />
          )}

          {requiresParcelPoint && cart && (
            <GlsDeliveryPointPicker
              cart={cart}
              countryCode={countryCode.toUpperCase()}
              onSaved={refreshCart}
            />
          )}

          {requiresParcelPoint && !selectedPoint && (
            <Text className="text-sm text-neutral-500">
              Izberite GLS prevzemno mesto, preden nadaljujete.
            </Text>
          )}

          <Button
            onClick={handleContinue}
            isLoading={isLoading}
            disabled={!selectedMethodId || shippingOptions.length === 0 || !canContinue}
            className="w-full mt-2"
            size="large"
          >
            Nadaljuj na plačilo
          </Button>
        </div>
      ) : isCompleted && selectedOption ? (
        <div className="flex flex-col gap-1 pt-2">
          <div className="flex items-center justify-between">
            <span className="text-sm text-neutral-600">{selectedOption.name}</span>
            <span className="text-sm font-medium">
              {convertToLocale({
                amount: selectedOption.amount || 0,
                currency_code: cart?.currency_code || "EUR",
              })}
            </span>
          </div>
          {selectedPoint && (
            <span className="text-xs text-neutral-500">
              {formatGlsDeliveryPointLabel(selectedPoint)}
            </span>
          )}
        </div>
      ) : null}
    </Card>
  )
}

export default ExpressShipping

















